// ============================================================================
// landingContent — conteúdo das landing pages de SEO (Fase C).
// ----------------------------------------------------------------------------
// Páginas dedicadas por keyword de alta intenção (pesquisa de mercado), em
// PT/ES/EN. Cada página usa o MESMO slug com prefixo de idioma (/…, /es/…,
// /en/…) para o hreflang/canonical funcionarem via lib/i18nRoutes. O template
// é o componente LandingPage; aqui fica só o conteúdo.
// ============================================================================
import { DOC_PAGES, DOC_NAV, DOC_CONTENT } from "./landingContentDocs";
import { INFO_PAGES, INFO_NAV, INFO_CONTENT } from "./landingContentInfo";

// Rótulos de UI compartilhados entre todas as landing pages, por idioma.
export const LANDING_UI = {
  pt: {
    benefitsTitle: "Por que a Alpha",
    faqTitle: "Perguntas Frequentes",
    ctaSub: "Envie seu documento e receba o orçamento na hora, sem compromisso.",
    trust: ["Tradutores membros da ATA", "Aceito pelo USCIS", "Revisores nativos"],
    relatedTitle: "Serviços relacionados",
    homeLabel: "Início",
    servicesTitle: "Nossos serviços",
  },
  es: {
    benefitsTitle: "Por qué Alpha",
    faqTitle: "Preguntas Frecuentes",
    ctaSub: "Envía tu documento y recibe el presupuesto al instante, sin compromiso.",
    trust: ["Traductores miembros de la ATA", "Aceptado por USCIS", "Revisores nativos"],
    relatedTitle: "Servicios relacionados",
    homeLabel: "Inicio",
    servicesTitle: "Nuestros servicios",
  },
  en: {
    benefitsTitle: "Why Alpha",
    faqTitle: "FAQ",
    ctaSub: "Upload your document and get an instant quote, no obligation.",
    trust: ["ATA-member translators", "USCIS-accepted", "Native reviewers"],
    relatedTitle: "Related services",
    homeLabel: "Home",
    servicesTitle: "Our services",
  },
};

// Rótulos curtos de navegação (para links internos), por página × idioma.
export const NAV_LABELS = {
  "certified-uscis": { pt: "Tradução Certificada USCIS", es: "Traducción Certificada USCIS", en: "Certified Translation for USCIS" },
  "us-immigration": { pt: "Tradução para Imigração Americana", es: "Traducción para Inmigración a EE. UU.", en: "U.S. Immigration Translation" },
  "us-visa": { pt: "Tradução para Visto Americano", es: "Traducción para Visa Americana", en: "U.S. Visa Translation" },
  pricing: { pt: "Preço e Orçamento", es: "Precio y Presupuesto", en: "Pricing & Quote" },
  ...DOC_NAV,
  ...INFO_NAV,
};

// Ordem de listagem (também usada para o sitemap).
export const LANDING_PAGES = [
  { key: "certified-uscis", path: "/traducao-certificada-uscis" },
  { key: "us-immigration", path: "/traducao-para-imigracao-americana" },
  { key: "us-visa", path: "/traducao-para-visto-americano" },
  { key: "pricing", path: "/traducao-juramentada-preco" },
  ...DOC_PAGES,
  ...INFO_PAGES,
];

export const LANDING_PATHS = LANDING_PAGES.map((p) => p.path);

// Conteúdo por página × idioma.
export const LANDING_CONTENT = {
  "certified-uscis": {
    pt: {
      title: "Tradução Certificada para o USCIS",
      description:
        "Tradução certificada aceita pelo USCIS para imigração nos EUA. Tradutores membros da ATA, entrega digital e orçamento na hora. Português, Espanhol e Inglês.",
      h1: "Tradução Certificada aceita pelo USCIS",
      subtitle: "Traduções para imigração nos Estados Unidos, com aceitação garantida.",
      intro:
        "Toda tradução para o USCIS acompanha uma certificação de fidelidade assinada, feita por tradutores membros da ATA. Você envia o documento e recebe o orçamento na hora.",
      benefits: [
        { title: "Aceitação garantida", text: "Certificação nos padrões do USCIS, aceita para imigração, vistos e green card." },
        { title: "Tradutores da ATA", text: "Profissionais membros da Associação Americana de Tradutores e revisores nativos." },
        { title: "100% digital e rápido", text: "Envio online, orçamento instantâneo e entrega digital com assinatura válida." },
      ],
      ctaLabel: "Enviar documento e orçar",
      faq: [
        { q: "O que é uma tradução certificada para o USCIS?", a: "É a tradução em inglês acompanhada de uma declaração assinada de fidelidade e competência do tradutor, no formato que o USCIS exige." },
        { q: "Posso traduzir meu próprio documento?", a: "Não. O USCIS exige tradução feita por um tradutor qualificado; traduções do próprio requerente costumam ser rejeitadas." },
        { q: "Quanto tempo demora?", a: "O prazo depende do número de páginas e é informado no seu orçamento. Há opção de prioridade." },
      ],
    },
    es: {
      title: "Traducción Certificada para USCIS",
      description:
        "Traducción certificada aceptada por USCIS para inmigración en EE. UU. Traductores miembros de la ATA, entrega digital y presupuesto al instante.",
      h1: "Traducción Certificada aceptada por USCIS",
      subtitle: "Traducciones para inmigración en los Estados Unidos, con aceptación garantizada.",
      intro:
        "Cada traducción para USCIS incluye una certificación de fidelidad firmada, hecha por traductores miembros de la ATA. Envías el documento y recibes el presupuesto al instante.",
      benefits: [
        { title: "Aceptación garantizada", text: "Certificación según los estándares de USCIS, aceptada para inmigración, visas y green card." },
        { title: "Traductores de la ATA", text: "Profesionales miembros de la Asociación Americana de Traductores y revisores nativos." },
        { title: "100% digital y rápido", text: "Envío en línea, presupuesto instantáneo y entrega digital con firma válida." },
      ],
      ctaLabel: "Enviar documento y cotizar",
      faq: [
        { q: "¿Qué es una traducción certificada para USCIS?", a: "Es la traducción al inglés acompañada de una declaración firmada de fidelidad y competencia del traductor, en el formato que exige USCIS." },
        { q: "¿Puedo traducir mis propios documentos?", a: "No. USCIS exige una traducción hecha por un traductor competente; las traducciones del propio solicitante suelen ser rechazadas." },
        { q: "¿Cuánto tarda?", a: "El plazo depende del número de páginas y se informa en tu presupuesto. Hay opción de prioridad." },
      ],
    },
    en: {
      title: "Certified Translation for USCIS",
      description:
        "USCIS-accepted certified translation for U.S. immigration. ATA-member translators, digital delivery and instant quote. Portuguese, Spanish and English.",
      h1: "Certified Translation accepted by USCIS",
      subtitle: "Translations for U.S. immigration, with guaranteed acceptance.",
      intro:
        "Every USCIS translation includes a signed certification of accuracy by ATA-member translators. Upload your document and get an instant quote.",
      benefits: [
        { title: "Guaranteed acceptance", text: "Certification meeting USCIS standards, accepted for immigration, visas and green card." },
        { title: "ATA translators", text: "Members of the American Translators Association and native reviewers." },
        { title: "100% digital and fast", text: "Online upload, instant quote and digital delivery with a valid signature." },
      ],
      ctaLabel: "Upload document & get a quote",
      faq: [
        { q: "What is a certified translation for USCIS?", a: "It's the English translation with a signed statement of accuracy and competence by the translator, in the format USCIS requires." },
        { q: "Can I translate my own documents?", a: "No. USCIS requires a translation by a competent translator; self-translated documents are usually rejected." },
        { q: "How long does it take?", a: "Turnaround depends on the number of pages and is shown in your quote. Priority is available." },
      ],
    },
  },

  "us-immigration": {
    pt: {
      title: "Tradução para Imigração Americana",
      description:
        "Tradução de documentos para imigração nos EUA, aceita pelo USCIS. Certidões, diplomas, antecedentes e mais. Membros da ATA. Orçamento na hora.",
      h1: "Tradução para Imigração Americana",
      subtitle: "Documentos traduzidos e certificados para o seu processo nos Estados Unidos.",
      intro:
        "Reunimos tudo o que o USCIS e os consulados pedem: tradução certificada em inglês, com certificação assinada e aceitação garantida. Envie seus documentos e receba o orçamento na hora.",
      benefits: [
        { title: "Para todo o processo", text: "Green card, vistos, cidadania e ajuste de status — traduzimos os documentos exigidos." },
        { title: "Aceito pelo USCIS", text: "Certificação nos padrões exigidos, feita por tradutores membros da ATA." },
        { title: "Rápido e digital", text: "Orçamento instantâneo e entrega digital; opção de prioridade para prazos apertados." },
      ],
      ctaLabel: "Orçar minha tradução",
      faq: [
        { q: "Quais documentos preciso traduzir para imigração?", a: "Em geral, certidões de nascimento e casamento, antecedentes criminais, diplomas e históricos. Cada caso varia — orientamos você." },
        { q: "A tradução precisa ser certificada?", a: "Sim. Para o USCIS, a tradução deve vir com certificação assinada de fidelidade — é o que entregamos." },
        { q: "Vocês traduzem do português e do espanhol?", a: "Sim, para o inglês, com revisores nativos. Também fazemos o caminho inverso quando necessário." },
      ],
    },
    es: {
      title: "Traducción para Inmigración a EE. UU.",
      description:
        "Traducción de documentos para inmigración en EE. UU., aceptada por USCIS. Actas, diplomas, antecedentes y más. Miembros de la ATA. Presupuesto al instante.",
      h1: "Traducción para Inmigración a Estados Unidos",
      subtitle: "Documentos traducidos y certificados para tu trámite en los Estados Unidos.",
      intro:
        "Reunimos todo lo que USCIS y los consulados piden: traducción certificada al inglés, con certificación firmada y aceptación garantizada. Envía tus documentos y recibe el presupuesto al instante.",
      benefits: [
        { title: "Para todo el trámite", text: "Green card, visas, ciudadanía y ajuste de estatus — traducimos los documentos exigidos." },
        { title: "Aceptado por USCIS", text: "Certificación según los estándares exigidos, por traductores miembros de la ATA." },
        { title: "Rápido y digital", text: "Presupuesto instantáneo y entrega digital; opción de prioridad para plazos ajustados." },
      ],
      ctaLabel: "Cotizar mi traducción",
      faq: [
        { q: "¿Qué documentos necesito traducir para inmigración?", a: "Generalmente actas de nacimiento y matrimonio, antecedentes penales, diplomas y expedientes. Cada caso varía — te orientamos." },
        { q: "¿La traducción debe ser certificada?", a: "Sí. Para USCIS, la traducción debe incluir una certificación firmada de fidelidad — es lo que entregamos." },
        { q: "¿Traducen del español al inglés?", a: "Sí, con revisores nativos. También hacemos el sentido inverso cuando es necesario." },
      ],
    },
    en: {
      title: "Translation for U.S. Immigration",
      description:
        "Document translation for U.S. immigration, accepted by USCIS. Certificates, diplomas, background checks and more. ATA members. Instant quote.",
      h1: "Translation for U.S. Immigration",
      subtitle: "Documents translated and certified for your U.S. process.",
      intro:
        "We cover everything USCIS and consulates require: certified English translation with a signed certification and guaranteed acceptance. Upload your documents and get an instant quote.",
      benefits: [
        { title: "For the whole process", text: "Green card, visas, citizenship and adjustment of status — we translate the required documents." },
        { title: "USCIS-accepted", text: "Certification meeting required standards, by ATA-member translators." },
        { title: "Fast and digital", text: "Instant quote and digital delivery; priority available for tight deadlines." },
      ],
      ctaLabel: "Get my quote",
      faq: [
        { q: "Which documents do I need to translate for immigration?", a: "Usually birth and marriage certificates, background checks, diplomas and transcripts. Each case varies — we'll guide you." },
        { q: "Does the translation need to be certified?", a: "Yes. For USCIS, the translation must include a signed certification of accuracy — which is what we provide." },
        { q: "Do you translate from Portuguese and Spanish?", a: "Yes, into English, with native reviewers. We also do the reverse when needed." },
      ],
    },
  },

  "us-visa": {
    pt: {
      title: "Tradução para Visto Americano",
      description:
        "Tradução de documentos para visto americano, aceita por consulados e USCIS. Certificação assinada, membros da ATA e orçamento na hora.",
      h1: "Tradução para Visto Americano",
      subtitle: "Documentos prontos para o consulado e para o seu pedido de visto.",
      intro:
        "Traduzimos e certificamos os documentos exigidos no pedido de visto para os Estados Unidos, no formato aceito por consulados e pelo USCIS. Tudo online, com orçamento na hora.",
      benefits: [
        { title: "Aceito no consulado", text: "Tradução certificada em inglês, no padrão exigido nos pedidos de visto." },
        { title: "Feito por especialistas", text: "Tradutores membros da ATA e revisores nativos, com sigilo total." },
        { title: "No seu prazo", text: "Entrega digital e opção de prioridade para entrevistas próximas." },
      ],
      ctaLabel: "Orçar agora",
      faq: [
        { q: "Preciso traduzir os documentos para o visto?", a: "Documentos que não estejam em inglês normalmente precisam de tradução certificada. Orientamos conforme o tipo de visto." },
        { q: "A tradução vale para a entrevista no consulado?", a: "Sim. Entregamos a tradução certificada aceita nos processos consulares e no USCIS." },
        { q: "Consigo com urgência?", a: "Sim, temos opção de prioridade. O prazo é informado no orçamento, conforme o número de páginas." },
      ],
    },
    es: {
      title: "Traducción para Visa Americana",
      description:
        "Traducción de documentos para la visa americana, aceptada por consulados y USCIS. Certificación firmada, miembros de la ATA y presupuesto al instante.",
      h1: "Traducción para Visa Americana",
      subtitle: "Documentos listos para el consulado y para tu solicitud de visa.",
      intro:
        "Traducimos y certificamos los documentos exigidos en la solicitud de visa para los Estados Unidos, en el formato aceptado por consulados y USCIS. Todo en línea, con presupuesto al instante.",
      benefits: [
        { title: "Aceptado en el consulado", text: "Traducción certificada al inglés, en el formato exigido en las solicitudes de visa." },
        { title: "Hecho por especialistas", text: "Traductores miembros de la ATA y revisores nativos, con total confidencialidad." },
        { title: "En tu plazo", text: "Entrega digital y opción de prioridad para entrevistas próximas." },
      ],
      ctaLabel: "Cotizar ahora",
      faq: [
        { q: "¿Necesito traducir los documentos para la visa?", a: "Los documentos que no estén en inglés suelen requerir traducción certificada. Te orientamos según el tipo de visa." },
        { q: "¿La traducción sirve para la entrevista consular?", a: "Sí. Entregamos la traducción certificada aceptada en los procesos consulares y en USCIS." },
        { q: "¿Puedo obtenerla con urgencia?", a: "Sí, tenemos opción de prioridad. El plazo se informa en el presupuesto, según el número de páginas." },
      ],
    },
    en: {
      title: "Translation for a U.S. Visa",
      description:
        "Document translation for a U.S. visa, accepted by consulates and USCIS. Signed certification, ATA members and instant quote.",
      h1: "Translation for a U.S. Visa",
      subtitle: "Documents ready for the consulate and your visa application.",
      intro:
        "We translate and certify the documents required for your U.S. visa application, in the format accepted by consulates and USCIS. All online, with an instant quote.",
      benefits: [
        { title: "Consulate-ready", text: "Certified English translation in the format required for visa applications." },
        { title: "By specialists", text: "ATA-member translators and native reviewers, fully confidential." },
        { title: "On your timeline", text: "Digital delivery and priority option for upcoming interviews." },
      ],
      ctaLabel: "Get a quote now",
      faq: [
        { q: "Do I need to translate documents for the visa?", a: "Documents not in English usually require certified translation. We'll advise based on your visa type." },
        { q: "Is the translation valid for the consular interview?", a: "Yes. We deliver certified translation accepted in consular processes and by USCIS." },
        { q: "Can I get it urgently?", a: "Yes, we offer a priority option. Turnaround is shown in the quote, based on page count." },
      ],
    },
  },

  pricing: {
    pt: {
      title: "Tradução Juramentada: Preço e Orçamento na Hora",
      description:
        "Descubra o preço da sua tradução juramentada ou certificada com um orçamento instantâneo. Cobrança por página, sem surpresas. Membros da ATA.",
      h1: "Preço da Tradução Juramentada e Certificada",
      subtitle: "Orçamento instantâneo, cobrança por página e sem surpresas.",
      intro:
        "O valor depende do número de páginas, do par de idiomas e do tipo de serviço. Em vez de tabelas genéricas, você envia o documento e recebe o preço exato na hora, sem compromisso.",
      benefits: [
        { title: "Cobrança por página", text: "Preço transparente por página traduzida — você sabe o total antes de fechar." },
        { title: "Orçamento na hora", text: "Envie o documento e receba o valor exato em segundos, sem compromisso." },
        { title: "Prioridade opcional", text: "Precisa para ontem? Há opção de entrega prioritária com prazo reduzido." },
      ],
      ctaLabel: "Ver meu preço agora",
      faq: [
        { q: "Como é calculado o preço?", a: "Por página, considerando o par de idiomas e o tipo de serviço (juramentada, certificada, simples). O total aparece no orçamento instantâneo." },
        { q: "É cobrado por página ou por palavra?", a: "Por página. Assim você sabe o valor total antes de aprovar, sem surpresas." },
        { q: "O orçamento tem custo?", a: "Não. Enviar o documento e ver o preço é gratuito e sem compromisso." },
      ],
    },
    es: {
      title: "Traducción Certificada: Precio y Presupuesto al Instante",
      description:
        "Conoce el precio de tu traducción certificada con un presupuesto instantáneo. Cobro por página, sin sorpresas. Miembros de la ATA.",
      h1: "Precio de la Traducción Certificada",
      subtitle: "Presupuesto al instante, cobro por página y sin sorpresas.",
      intro:
        "El valor depende del número de páginas, del par de idiomas y del tipo de servicio. En lugar de tablas genéricas, envías el documento y recibes el precio exacto al instante, sin compromiso.",
      benefits: [
        { title: "Cobro por página", text: "Precio transparente por página traducida — sabes el total antes de confirmar." },
        { title: "Presupuesto al instante", text: "Envía el documento y recibe el valor exacto en segundos, sin compromiso." },
        { title: "Prioridad opcional", text: "¿Lo necesitas ya? Hay opción de entrega prioritaria con plazo reducido." },
      ],
      ctaLabel: "Ver mi precio ahora",
      faq: [
        { q: "¿Cómo se calcula el precio?", a: "Por página, según el par de idiomas y el tipo de servicio (certificada, jurada, simple). El total aparece en el presupuesto instantáneo." },
        { q: "¿Se cobra por página o por palabra?", a: "Por página. Así sabes el total antes de aprobar, sin sorpresas." },
        { q: "¿El presupuesto tiene costo?", a: "No. Enviar el documento y ver el precio es gratis y sin compromiso." },
      ],
    },
    en: {
      title: "Certified Translation: Price and Instant Quote",
      description:
        "Find your certified translation price with an instant quote. Per-page pricing, no surprises. ATA members.",
      h1: "Certified Translation Pricing",
      subtitle: "Instant quote, per-page pricing and no surprises.",
      intro:
        "The price depends on the number of pages, the language pair and the service type. Instead of generic tables, you upload your document and get the exact price instantly, with no obligation.",
      benefits: [
        { title: "Per-page pricing", text: "Transparent price per translated page — you know the total before you commit." },
        { title: "Instant quote", text: "Upload the document and get the exact price in seconds, no obligation." },
        { title: "Optional priority", text: "Need it fast? A priority option with reduced turnaround is available." },
      ],
      ctaLabel: "See my price now",
      faq: [
        { q: "How is the price calculated?", a: "Per page, based on the language pair and service type (certified, sworn, simple). The total appears in your instant quote." },
        { q: "Is it charged per page or per word?", a: "Per page. That way you know the total before approving, with no surprises." },
        { q: "Is the quote free?", a: "Yes. Uploading the document and seeing the price is free and with no obligation." },
      ],
    },
  },
  ...DOC_CONTENT,
  ...INFO_CONTENT,
};
